import React, { useCallback, useEffect, useState } from 'react'
import { DataTable } from '@/components/ui/DataTable'
import { Modal } from '@/components/ui/Modal'
import { Toast } from '@/components/ui/Toast'
import { updateApp } from '@/api/apps'
import { useAppStore } from '@/store/appStore'
import { API_ORIGIN } from '@/api/client'
import { PlatformApp } from '@/types/index'

// Feature #385 (Epic #381): Settings › Apps is the per-environment on/off
// switch for each module. Until now this page was April-era scaffolding —
// a hard-coded list ("Core Platform / Reporting / Integration Hub") whose
// checkbox only changed React state. It now reads the `lab_app` registry
// (#382) and a toggle here is what makes the backend gate (#383) and the
// SPA guards (#384) switch a module off for everyone in this environment.
//
// The old page also showed a made-up platform version, a hard-coded
// "Preprod" badge and four documentation links that all pointed at "/".
// Those are gone. The one card kept — Environment — is derived from the
// API origin the bundle was built with, which is the only honest source.

const describeEnvironment = (origin: string): { label: string; tone: 'prod' | 'preprod' | 'local' } => {
  const host = origin.replace(/^https?:\/\//, '')
  if (!host || host.startsWith('localhost') || host.startsWith('127.')) {
    return { label: 'Local', tone: 'local' }
  }
  if (host.includes('preprod')) {
    return { label: 'Preprod', tone: 'preprod' }
  }
  return { label: 'Production', tone: 'prod' }
}

const toneStyles: Record<'prod' | 'preprod' | 'local', { bg: string; fg: string }> = {
  prod: { bg: '#fee2e2', fg: '#b91c1c' },
  preprod: { bg: '#fef3c7', fg: '#b45309' },
  local: { bg: '#e0f2fe', fg: '#0369a1' },
}

const AppSettings: React.FC = () => {
  const apps = useAppStore((s) => s.apps)
  const loaded = useAppStore((s) => s.loaded)
  const load = useAppStore((s) => s.load)
  const reload = useAppStore((s) => s.reload)

  const [pendingDisable, setPendingDisable] = useState<PlatformApp | null>(null)
  const [busyKey, setBusyKey] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [toast, setToast] = useState<string | null>(null)
  const dismissToast = useCallback(() => setToast(null), [])

  useEffect(() => {
    void load()
  }, [load])

  const env = describeEnvironment(API_ORIGIN)

  const applyToggle = async (app: PlatformApp, isEnabled: boolean) => {
    setBusyKey(app.key)
    setError(null)
    // Optimistic: flip the row locally so the switch moves at once; reload()
    // afterwards replaces it with the server's view and bumps navVersion so
    // the sidebar refetches.
    useAppStore.setState((s) => ({
      apps: s.apps.map((a) => (a.key === app.key ? { ...a, isEnabled } : a)),
    }))
    try {
      await updateApp(app.key, { isEnabled })
      await reload()
      setToast(`${app.name} is now ${isEnabled ? 'enabled' : 'disabled'} in this environment.`)
    } catch (err) {
      // Roll back to what the server last told us.
      await reload()
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
        (err as Error)?.message ??
        'Unknown error'
      setError(`Could not update ${app.name}: ${detail}`)
    } finally {
      setBusyKey(null)
    }
  }

  const requestToggle = (app: PlatformApp) => {
    if (!app.isToggleable || busyKey) return
    if (app.isEnabled) {
      // Switching off affects everyone in this environment immediately —
      // confirm. Switching on needs no confirmation.
      setPendingDisable(app)
    } else {
      void applyToggle(app, true)
    }
  }

  const confirmDisable = () => {
    const app = pendingDisable
    setPendingDisable(null)
    if (app) void applyToggle(app, false)
  }

  return (
    <div>
      <h2 style={{ fontSize: '18px', fontWeight: 600, margin: '0 0 8px 0', color: 'var(--color-black)' }}>
        App settings
      </h2>
      <p style={{ margin: '0 0 24px 0', fontSize: '14px', color: 'var(--color-dark-grey)', maxWidth: '720px' }}>
        Switch a module on or off for this environment. A module that is off has no menu entries, its
        pages show &ldquo;not available&rdquo;, and its API refuses requests. Preprod and production are
        switched independently.
      </p>

      {error && (
        <div
          role="alert"
          style={{
            marginBottom: '16px',
            padding: '12px 16px',
            borderRadius: '8px',
            backgroundColor: '#fee2e2',
            color: '#b91c1c',
            fontSize: '14px',
          }}
        >
          {error}
        </div>
      )}

      {/* Registered apps table */}
      <div style={{ marginBottom: '32px' }}>
        <h3 style={{ fontSize: '16px', fontWeight: 600, margin: '0 0 16px 0', color: 'var(--color-black)' }}>
          Registered apps
        </h3>

        {!loaded ? (
          <div style={{ padding: '24px', color: 'var(--color-dark-grey)', fontSize: '14px' }}>Loading…</div>
        ) : (
          <DataTable
            columns={[
              {
                key: 'name',
                header: 'App name',
                render: (value: string, row: PlatformApp) => (
                  <div>
                    <div style={{ fontWeight: 500 }}>{value}</div>
                    {row.description && (
                      <div style={{ fontSize: '12px', color: 'var(--color-dark-grey)', marginTop: '2px' }}>
                        {row.description}
                      </div>
                    )}
                  </div>
                ),
              },
              {
                key: 'key',
                header: 'Key',
                render: (value: string) => (
                  <code style={{ fontSize: '12px', backgroundColor: '#f3f4f6', padding: '2px 6px', borderRadius: '3px' }}>
                    {value}
                  </code>
                ),
              },
              {
                key: 'isEnabled',
                header: 'Status',
                render: (value: boolean, row: PlatformApp) => (
                  <label
                    title={
                      row.isToggleable
                        ? undefined
                        : 'The core platform cannot be switched off.'
                    }
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      cursor: row.isToggleable && !busyKey ? 'pointer' : 'not-allowed',
                      opacity: row.isToggleable ? 1 : 0.6,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={value}
                      disabled={!row.isToggleable || busyKey === row.key}
                      onChange={() => requestToggle(row)}
                      aria-label={`${row.name} ${value ? 'enabled' : 'disabled'}`}
                      style={{ cursor: 'inherit' }}
                    />
                    <span style={{ fontSize: '12px', color: value ? '#15803d' : '#6b7280' }}>
                      {busyKey === row.key ? 'Saving…' : value ? 'Enabled' : 'Disabled'}
                    </span>
                  </label>
                ),
              },
            ]}
            data={apps}
            rowKey="key"
          />
        )}
      </div>

      {/* Environment */}
      <div>
        <h3 style={{ fontSize: '16px', fontWeight: 600, margin: '0 0 16px 0', color: 'var(--color-black)' }}>
          Environment
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
          <div style={{ padding: '16px', border: '1px solid var(--color-light-grey)', borderRadius: '8px', backgroundColor: 'var(--color-white)' }}>
            <div style={{ fontSize: '12px', color: 'var(--color-dark-grey)', marginBottom: '4px', fontWeight: 500 }}>
              This environment
            </div>
            <div style={{ fontSize: '16px', fontWeight: 600, color: 'var(--color-black)', marginBottom: '8px' }}>
              {env.label}
            </div>
            <span
              style={{
                display: 'inline-block',
                padding: '2px 8px',
                backgroundColor: toneStyles[env.tone].bg,
                color: toneStyles[env.tone].fg,
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: 600,
              }}
            >
              {API_ORIGIN.replace(/^https?:\/\//, '') || 'same origin'}
            </span>
          </div>

          <div style={{ padding: '16px', border: '1px solid var(--color-light-grey)', borderRadius: '8px', backgroundColor: 'var(--color-white)' }}>
            <div style={{ fontSize: '12px', color: 'var(--color-dark-grey)', marginBottom: '4px', fontWeight: 500 }}>
              Registered apps
            </div>
            <div style={{ fontSize: '16px', fontWeight: 600, color: 'var(--color-black)', marginBottom: '8px' }}>
              {apps.length}
            </div>
            <p style={{ margin: 0, fontSize: '12px', color: 'var(--color-dark-grey)' }}>
              {apps.filter((a) => a.isEnabled).length} enabled
            </p>
          </div>
        </div>
      </div>

      <Modal
        isOpen={pendingDisable !== null}
        onClose={() => setPendingDisable(null)}
        title={pendingDisable ? `Switch off ${pendingDisable.name}?` : ''}
        size="sm"
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <button
              type="button"
              onClick={() => setPendingDisable(null)}
              style={{
                padding: '8px 16px',
                border: '1px solid var(--color-light-grey)',
                borderRadius: '6px',
                backgroundColor: 'var(--color-white)',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirmDisable}
              style={{
                padding: '8px 16px',
                border: 'none',
                borderRadius: '6px',
                backgroundColor: '#b91c1c',
                color: 'var(--color-white)',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              Switch off
            </button>
          </div>
        }
      >
        <p style={{ margin: 0, fontSize: '14px', color: 'var(--color-black)', lineHeight: 1.5 }}>
          This switches off <strong>{pendingDisable?.name}</strong> for every user in{' '}
          <strong>{env.label.toLowerCase()}</strong> immediately: its menu entries disappear, its pages
          show &ldquo;not available&rdquo;, and its API refuses requests. No data is removed, and you can
          switch it back on from this page at any time.
        </p>
      </Modal>

      <Toast message={toast} onDismiss={dismissToast} />
    </div>
  )
}

export default AppSettings
